import type { ViewportsConfigurations } from 'domains/viewer/ViewportsConfigurations';
import type {
  HangingProtocolPayload,
  ViewType,
  IViewportConfiguration,
  SeriesCriteriaCategory,
} from 'generated/graphql';
import uniq from 'ramda/es/uniq';
import { trim } from 'utils/string';
import { SERIES_CRITERIA_CATEGORY_MAP } from './seriesCriteriaCategoryMap';

/** Mutable and non-nullable version of IViewportConfiguration */
export type MutableViewportConfig = {
  id: string;
  viewType: ViewType;
  comparisonIndex: number | null;
  seriesId: string | undefined;
  seriesDescriptions: string[];
  viewportMirrorGroupId?: number;
  criteria?: ReadonlyArray<{ category: SeriesCriteriaCategory; value: string }>;
};

type MirrorGroupKeyType = `${string}|${string}`;
/** Creates a mirror group key from seriesId and comparisonIndex.
 * null (current exam) and 0 (first prior) must be distinct keys. */
function createMirrorGroupKey(
  seriesId: string,
  comparisonIndex: number | null
): MirrorGroupKeyType {
  return `${seriesId}|${comparisonIndex === null ? 'current' : comparisonIndex}`;
}

/**
 * Processes viewports to identify mirror groups and apply mirror group IDs.
 * Mirror groups are viewports that display the same series in the same comparison context.
 *
 * Algorithm:
 * 1. Group viewports by (seriesId, comparisonIndex) pairs
 * 2. For groups with 2+ viewports, assign a shared mirror group ID (1-100)
 * 3. Use the first viewport (by layout order) as the source for shared data
 * 4. All members receive the same mirrorGroupId, seriesId, and seriesDescriptions
 * 5. All viewports are kept in the output (no filtering)
 *
 * @param viewportConfigs - Array of viewport configurations sorted by layout order
 * @returns Array of viewport configurations with mirror groups applied (all viewports retained)
 */
export const applyMirrorGroups = (
  viewportConfigs: MutableViewportConfig[]
): MutableViewportConfig[] => {
  // Group viewports by (seriesId, comparisonIndex) pairs
  const groupsByKey = new Map<MirrorGroupKeyType, MutableViewportConfig[]>();

  for (const viewport of viewportConfigs) {
    // Only group viewports that have a seriesId
    if (!viewport.seriesId) {
      continue;
    }
    const groupKey = createMirrorGroupKey(viewport.seriesId, viewport.comparisonIndex);
    const existing = groupsByKey.get(groupKey) ?? [];
    existing.push(viewport);
    groupsByKey.set(groupKey, existing);
  }

  // Assign mirror group IDs to groups with multiple members
  // Create a simple map: viewportId → mirrorGroupId
  let nextMirrorGroupId = 1;
  const viewportMirrorGroupInfo = new Map<string, number>();

  for (const [groupKey, viewportsInGroup] of groupsByKey.entries()) {
    if (viewportsInGroup.length > 1) {
      if (nextMirrorGroupId > 100) {
        throw new Error(
          'Cannot create more than 100 mirror groups in a hanging protocol. This limit has been exceeded.'
        );
      }

      const mirrorGroupId = nextMirrorGroupId++;

      // Assign mirror group ID to all members
      for (const viewport of viewportsInGroup) {
        viewportMirrorGroupInfo.set(viewport.id, mirrorGroupId);
      }
    }
  }

  // Apply mirror group IDs
  return viewportConfigs.map((viewport) => {
    const mirrorGroupId = viewportMirrorGroupInfo.get(viewport.id);

    if (mirrorGroupId !== undefined) {
      return {
        ...viewport,
        viewportMirrorGroupId: mirrorGroupId,
      };
    }

    // Non-mirror viewport: return as-is
    return viewport;
  });
};

/**
 * Creates a stable grouping key from a viewport's series criteria. Viewports with the same
 * criteria key should be assigned the same series by the backend (mirror groups).
 */
export function createCriteriaGroupKey(
  criteria: Nullable<ReadonlyArray<{ category: string; value: string }>>
): string | undefined {
  if (!criteria?.length) return undefined;
  return criteria
    .map((c) => `${c.category}:${c.value}`)
    .sort()
    .join('|');
}

/**
 * Applies mirror group IDs to a list of criteria-only viewports (flat or stepped HPs), where
 * viewports have no resolved series smid yet -- grouping is keyed off a stable key derived from each viewport's
 * criteria instead. Shared by {@link generatePayload} (flat HPs) and
 *
 * Returns mirror group IDs in the same order as `entries` (or `null` for a viewport that isn't
 * part of a mirror group).
 */
export function applyMirrorGroupsByCriteria(
  entries: ReadonlyArray<{
    id: string;
    comparisonIndex: number | null;
    criteria: Nullable<ReadonlyArray<{ category: string; value: string }>>;
  }>
): Array<number | null> {
  const configs: MutableViewportConfig[] = entries.map((entry) => ({
    id: entry.id,
    viewType: 'TWO_D_DRE' as ViewType,
    comparisonIndex: entry.comparisonIndex,
    seriesId: createCriteriaGroupKey(entry.criteria),
    seriesDescriptions: [],
  }));

  return applyMirrorGroups(configs).map((config) => config.viewportMirrorGroupId ?? null);
}

export const viewportsConfigToHangingProtocol = (
  viewportsConfigurations: ViewportsConfigurations
): HangingProtocolPayload['data']['viewportsConfigurations'] => {
  // First pass: collect all viewport configs
  const viewportConfigs = Object.entries(viewportsConfigurations).reduce<MutableViewportConfig[]>(
    (acc, [key, config]) => {
      if (config == null) {
        return acc;
      }

      const {
        series,
        seriesDescriptions = [],
        viewType,
        comparisonIndex = null,
        stack,
        seriesCriteria,
      } = config;

      // Transform seriesCriteria to criteria array format for the GraphQL input
      const criteria = seriesCriteria
        ? Object.entries(seriesCriteria).flatMap(([key, values]) => {
            const category =
              SERIES_CRITERIA_CATEGORY_MAP[key as keyof typeof SERIES_CRITERIA_CATEGORY_MAP];
            if (!category || !Array.isArray(values)) return [];
            return values
              .filter((v) => v.ontology_id)
              .map((v) => ({ category, value: v.ontology_id as string }));
          })
        : undefined;
      // There are a couple edge cases we want to handle, here:
      // 1. The viewport is "blank" and does not have a series
      // 2. The series does not have a description
      // For both of these cases, we will either set the existing [...seriesDescriptions] or provide a default [].
      const isValidDescription = series?.description != null && series?.description !== '';

      // The stack's frame data is more specific and authoritative about what series is actually
      // being shown in that viewport. However, not all viewports have stacks (MPRs or other view
      // types that don't use the stack structure). In those cases, we'll use the viewport's series
      // reference.
      const seriesId = stack?.frames?.[0].series.smid ?? series?.smid;

      return [
        ...acc,
        {
          id: key,
          // ViewportTypeKeys and ViewType represent the same values, cast for compatibility
          viewType: viewType as ViewType,
          comparisonIndex,
          seriesId,
          seriesDescriptions: uniq(
            isValidDescription
              ? [...seriesDescriptions, trim(series?.description)]
              : seriesDescriptions
          ),
          ...(criteria && criteria.length > 0 ? { criteria } : {}),
        },
      ];
    },
    []
  );

  // Sort viewports by layout order (ID determines position)
  // This ensures a consistent "first member" selection for each mirror group
  viewportConfigs.sort((a, b) => a.id.localeCompare(b.id));

  // Apply mirror group logic: identify groups and assign IDs
  // The cast below is required because we are transforming from a ViewportConfig
  // (mutable/non-nullable) to an IViewportConfiguration (readonly/nullable, GraphQL-compatible).
  // They are structurally the same at runtime.
  return applyMirrorGroups(viewportConfigs) as IViewportConfiguration[];
};
